import React from 'react';
import {
  Autocomplete as MuiAutocomplete,
  AutocompleteProps as MuiAutocompleteProps,
  AutocompleteRenderInputParams,
  InputLabel,
  InputLabelProps,
  Select as MuiSelect,
  SelectProps as MuiSelectProps,
  Stack,
  StackProps,
  TextField as MuiTextField,
  TextFieldProps as MuiTextFieldProps,
} from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';

export type FieldSurface = 'normal' | 'sunken';

export interface FormFieldProps extends StackProps {
  clampLabel?: boolean;
}

const spacedFormFieldStyles = { gap: 0.75, minWidth: 0 };
const clampedFormFieldStyles = {
  gap: 0,
  minWidth: 0,
  '& [data-form-field-label]': { ml: '4px' },
};
const getDisabledBorderColor = (theme: Theme) =>
  `${theme.palette.softBorder} !important`;

export const FormField = React.forwardRef<HTMLDivElement, FormFieldProps>(
  ({ clampLabel = false, direction = 'column', sx, ...props }, ref) => {
    const spacingStyles = clampLabel
      ? clampedFormFieldStyles
      : spacedFormFieldStyles;
    const combinedStyles = (sx
      ? [spacingStyles, sx]
      : spacingStyles) as SxProps<Theme>;

    return (
      <Stack {...props} ref={ref} direction={direction} sx={combinedStyles} />
    );
  }
);

FormField.displayName = 'FormField';

export const getOutlinedInputSurfaceStyles = (surface: FieldSurface) => ({
  boxSizing: 'border-box',
  minHeight: 40,
  bgcolor: surface === 'sunken' ? 'sunken' : 'background.paper',
  boxShadow:
    surface === 'sunken' ? 'inset 0 1px 2px rgba(0, 0, 0, 0.14)' : 'none',
  borderRadius: surface === 'sunken' ? 2 : 1.5,
  '&.MuiInputBase-adornedStart': {
    paddingLeft: 1.75,
  },
  '& .MuiInputAdornment-positionStart': {
    marginLeft: 0,
    marginRight: 1,
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: 'softBorder',
  },
  '&:not(.Mui-disabled):hover .MuiOutlinedInput-notchedOutline': {
    borderColor: surface === 'sunken' ? 'softBorder' : 'outline',
  },
  '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
    // Autocomplete and Select inject different disabled selectors. The shared
    // surface must win over both component-specific MUI defaults.
    borderColor: getDisabledBorderColor,
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderWidth: 1,
    borderColor: 'primary.main',
  },
});

export const getFieldSurfaceStyles = (surface: FieldSurface) => ({
  '& .MuiOutlinedInput-root': getOutlinedInputSurfaceStyles(surface),
  '& .MuiAutocomplete-inputRoot.MuiOutlinedInput-root.MuiInputBase-adornedStart': {
    paddingLeft: 1.75,
  },
  '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline': {
    borderColor: getDisabledBorderColor,
  },
});

export interface TextFieldProps extends Omit<MuiTextFieldProps, 'variant'> {
  surface?: FieldSurface;
}

export const TextField = React.forwardRef<HTMLDivElement, TextFieldProps>(
  ({ size = 'small', surface = 'normal', sx, ...props }, ref) => {
    const surfaceStyles = getFieldSurfaceStyles(surface);
    const combinedStyles = (sx
      ? [surfaceStyles, sx]
      : surfaceStyles) as SxProps<Theme>;

    return (
      <MuiTextField
        {...props}
        ref={ref}
        size={size}
        variant="outlined"
        sx={combinedStyles}
      />
    );
  }
);

TextField.displayName = 'TextField';

export interface SelectProps extends MuiSelectProps {
  surface?: FieldSurface;
}

export const Select = React.forwardRef<HTMLDivElement, SelectProps>(
  ({ size = 'small', surface = 'normal', sx, ...props }, ref) => {
    const surfaceStyles = getOutlinedInputSurfaceStyles(surface);
    const combinedStyles = (sx
      ? [surfaceStyles, sx]
      : surfaceStyles) as SxProps<Theme>;

    return (
      <MuiSelect
        {...props}
        ref={ref}
        size={size}
        variant="outlined"
        sx={combinedStyles}
      />
    );
  }
);

Select.displayName = 'Select';

const fieldLabelStyles = {
  position: 'static',
  transform: 'none',
  fontSize: '0.875rem',
  fontWeight: 600,
  lineHeight: 1.4,
  overflow: 'visible',
};

export const FieldLabel = React.forwardRef<HTMLLabelElement, InputLabelProps>(
  ({ shrink = true, sx, ...props }, ref) => {
    const combinedStyles = (sx
      ? [fieldLabelStyles, sx]
      : fieldLabelStyles) as SxProps<Theme>;

    return (
      <InputLabel
        {...props}
        ref={ref}
        data-form-field-label
        shrink={shrink}
        sx={combinedStyles}
      />
    );
  }
);

FieldLabel.displayName = 'FieldLabel';

export interface AutocompleteProps<
  Value,
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = false
> extends Omit<
    MuiAutocompleteProps<Value, Multiple, DisableClearable, FreeSolo>,
    'renderInput'
  > {
  surface?: FieldSurface;
  renderInput?: MuiAutocompleteProps<
    Value,
    Multiple,
    DisableClearable,
    FreeSolo
  >['renderInput'];
  textFieldProps?: Omit<TextFieldProps, 'surface'>;
}

export const Autocomplete = <
  Value,
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = false
>({
  size = 'small',
  surface = 'normal',
  renderInput: renderInputOverride,
  sx,
  textFieldProps,
  ...props
}: AutocompleteProps<Value, Multiple, DisableClearable, FreeSolo>) => {
  const surfaceStyles = getFieldSurfaceStyles(surface);
  const combinedStyles = (sx
    ? [surfaceStyles, sx]
    : surfaceStyles) as SxProps<Theme>;
  const renderInput =
    renderInputOverride ||
    ((params: AutocompleteRenderInputParams) => (
      <TextField
        {...params}
        {...textFieldProps}
        size={size}
        surface={surface}
      />
    ));

  return (
    <MuiAutocomplete<Value, Multiple, DisableClearable, FreeSolo>
      {...props}
      size={size}
      renderInput={renderInput}
      sx={combinedStyles}
    />
  );
};

type SurfaceTextFieldProps = TextFieldProps;
type SurfaceSelectProps = SelectProps;
type SurfaceAutocompleteProps<
  Value,
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = false
> = AutocompleteProps<Value, Multiple, DisableClearable, FreeSolo>;

const SurfaceTextField = TextField;
const SurfaceSelect = Select;
const SurfaceAutocomplete = Autocomplete;

export { SurfaceAutocomplete, SurfaceSelect, SurfaceTextField };
export type {
  SurfaceAutocompleteProps,
  SurfaceSelectProps,
  SurfaceTextFieldProps,
};
